import { useQueryClient } from '@tanstack/react-query';
import {
	type BaseSyntheticEvent,
	createContext,
	type ReactNode,
	useContext,
	useEffect,
} from 'react';
import {
	type OsobyPodnikatelDto,
	ppdfConsentDetailQuery,
	type PpdfSouhlasySouhlasDto,
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQuery,
} from '@gov-nx/api';
import { useIdentity } from '@gov-nx/auth/common';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	useProcessControl,
	useRezaSwitcher,
	useRobPersonQuery,
	useServiceAuthorization,
	useWizard,
	type WizardFormStep,
} from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import { isNotEmptyArray } from '@gov-nx/core/types';
import { useRosBusinessPersonQuery } from '../../nastaveni/kontaktni-udaje-ros/useRosBusinessPersonQuery';
import { usePpdfSouhlasyLocale } from '../usePpdfSouhlasyLocale';
import {
	type PpdfConsentsUdeleniContext,
	type PpdfSouhlasyFormControls,
	PpdfSouhlasyTypOsobyEnum,
	type ZapisForm,
} from './context.types';
import { mapConsentDtoToForm } from './mapConsentDtoToForm';
import { prepareSubmitData } from './prepareSubmitData';
import { useFormStep1 } from './useFormStep1';
import { useFormStep2 } from './useFormStep2';
import {
	type PpdfSouhlasyUdeleniFormStep3,
	useFormStep3,
} from './useFormStep3';
import { useOvmDataQuery } from './useOvmDataQuery';
import { ServiceCode } from '@gov-nx/module/service';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const ServiceContext =
	createContext<Nullable<PpdfConsentsUdeleniContext>>(null);

export interface PpdfSouhlasyUdeleniPrefillForm {
	kodSpravceUdaju?: string;
	kodAgendySpravceUdaju?: string;
	kodPrijemceUdaju?: string;
	datumDo?: string;
	kodAgendyPrijemceUdaju?: string[];
	udaje?: string[];
}

interface Props {
	consentId?: string;
	children: ReactNode;
	revoke?: boolean;
	prefillForm?: PpdfSouhlasyUdeleniPrefillForm;
}

const AUTHORIZATION_SERVICE_NAME_PPDF_POSKYTNUTI = 'PODANI_PPDF_POSKYTNUTI';
const AUTHORIZATION_SERVICE_NAME_PPDF_ODVOLANI = 'PODANI_PPDF_ODVOLANI';

export const PpdfSouhlasyUdeleniContextProvider = ({
	children,
	consentId,
	revoke,
	prefillForm,
}: Props) => {
	const AUTHORIZATION_SERVICE_NAME = revoke
		? AUTHORIZATION_SERVICE_NAME_PPDF_ODVOLANI
		: AUTHORIZATION_SERVICE_NAME_PPDF_POSKYTNUTI;
	const personQuery = useRobPersonQuery();
	const { t } = usePpdfSouhlasyLocale();
	const { toastMessageSuccess } = useMessageEvents();
	const loginInfo = useIdentity();
	const queryClient = useQueryClient();
	const rezaSwitcher = useRezaSwitcher({ code: ServiceCode['ppdf-souhlasy'] });
	const authContext = useAutorizaceContext();

	const { setControls, controls } = useProcessControl<PpdfSouhlasyFormControls>(
		{
			initialLoading: true,
			createLoading: false,
			saveLoading: false,
			deleteLoading: false,
		}
	);

	const { rosContacts } = useRosBusinessPersonQuery();

	const queryPpdfConsentDetail = usePoQuery({
		queryKey: ['ppdf-souhlasy', consentId],
		queryFn: () =>
			consentId ? ppdfConsentDetailQuery(axiosInstance, consentId) : {},
	});

	const { isLoading: toOvmOptionsIsLoading, ovmOptions: toOvmOptions } =
		useOvmDataQuery({ maAgendy: false });
	const { axiosInstance } = useAxiosInstance();

	const submitMutation = usePoMutation({
		mutationKey: rezaSwitcher.blockingMutationKey,
		mutationFn: async (variables: ZapisForm) => {
			const dataBoxInfo = step1.dataBoxes.find(
				(item) => item.datovaSchrankaId === poDataBoxId
			);
			const poIco = getRosContactByDataBoxId(variables.poDataBoxId)?.ico;
			const { params, body } = prepareSubmitData(variables, dataBoxInfo, poIco);
			return sendSubmissionQuery(axiosInstance)({
				params,
				body: {
					...(!(consentId || revoke) && { kodPodani: 'PPDF_POSKYTNUTI' }),
					...(consentId && { id: consentId, kodPodani: 'PPDF_ODVOLANI' }),
					...(revoke && { kodPodani: 'PPDF_ODVOLANI' }),
					...body,
				},
			});
		},
		onSuccess: (data) => {
			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('zpravy.odeslano'));
				queryClient.invalidateQueries({
					queryKey: ['ppdf-consent'],
				});
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const setToOvm = (kodPrijemceUdaju: string | undefined) => {
		if (!kodPrijemceUdaju) {
			return;
		}
		const selectedToOvm = toOvmOptions.find(
			(ovm) => ovm.value === kodPrijemceUdaju
		);
		if (!selectedToOvm) {
			return;
		}
		step2.form.formMethods.setValue('toOvmId', {
			selected: {
				kod: selectedToOvm.value,
				nazev: selectedToOvm.label,
			},
			input: selectedToOvm.label,
		});
	};

	const setDateOfValidityConsent = (date?: string) => {
		if (!date) {
			return;
		}

		step2.form.formMethods.setValue('consentValidityInterval', 'URCITY');
		step2.form.formMethods.setValue('dateOfValidityConsent', new Date(date));
	};

	const setFormValuesFromQueryParams = () => {
		if (prefillForm == null) {
			return;
		}

		if (prefillForm.kodSpravceUdaju) {
			step2.form.formMethods.setValue('fromOvmId', prefillForm.kodSpravceUdaju);
		}

		if (isNotEmptyArray(prefillForm.kodAgendyPrijemceUdaju)) {
			step2.form.formMethods.setValue('toAgendaId', [
				...prefillForm.kodAgendyPrijemceUdaju,
			]);
		}

		if (prefillForm.kodAgendySpravceUdaju) {
			step2.form.formMethods.setValue(
				'fromAgendaId',
				prefillForm.kodAgendySpravceUdaju
			);
		}

		setToOvm(prefillForm.kodPrijemceUdaju);

		if (isNotEmptyArray(prefillForm.udaje)) {
			step2.form.formMethods.setValue('sharedAgendaMultiselect', [
				...prefillForm.udaje,
			]);
		}

		setDateOfValidityConsent(prefillForm.datumDo);
	};

	const setFormValuesFromDto = (dto: PpdfSouhlasySouhlasDto) => {
		const prefill = mapConsentDtoToForm(dto);

		if (prefill.personType) {
			step1.form.formMethods.setValue('personType', prefill.personType);
		}

		if (prefill.poDataBoxId) {
			step1.form.formMethods.setValue('poDataBoxId', prefill.poDataBoxId);
		}

		if (prefill.fromOvmId) {
			step2.form.formMethods.setValue('fromOvmId', prefill.fromOvmId);
		}

		if (prefill.fromAgendaId) {
			step2.form.formMethods.setValue('fromAgendaId', prefill.fromAgendaId);
		}

		if (prefill.toAgendaId) {
			step2.form.formMethods.setValue('toAgendaId', prefill.toAgendaId);
		}

		if (prefill.sharedAgendaMultiselect) {
			step2.form.formMethods.setValue(
				'sharedAgendaMultiselect',
				prefill.sharedAgendaMultiselect
			);
		}

		setToOvm(dto.kodPrijemceUdaju);
		setDateOfValidityConsent(dto.datumTrvaniUdeleniSouhlasu);
	};

	useEffect(
		() => {
			if (controls.initialLoading || toOvmOptionsIsLoading) return;
			if (queryPpdfConsentDetail.data && consentId) {
				setFormValuesFromDto(queryPpdfConsentDetail.data);
			} else {
				step1.form.formMethods.setValue(
					'personType',
					PpdfSouhlasyTypOsobyEnum.FO
				);
				setFormValuesFromQueryParams();
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		[
			queryPpdfConsentDetail.data,
			consentId,
			controls.initialLoading,
			toOvmOptionsIsLoading,
		]
	);

	useEffect(() => {
		if (queryPpdfConsentDetail.isFetched && controls.initialLoading) {
			setControls({ initialLoading: false });
		}
	}, [queryPpdfConsentDetail.isFetched, controls.initialLoading, setControls]);

	const isSubmitting = submitMutation.isLoading;

	const step1 = useFormStep1({
		isDisabled: isSubmitting,
		databoxTypes: ['PFO', 'PO'],
	});

	const { poDataBoxId, personType } = step1.form.formMethods.watch();

	const step2 = useFormStep2({ isDisabled: isSubmitting });
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const step3 = useFormStep3({
		isDisabled: isSubmitting,
		isLowAuthorization,
		fixedDataBoxId:
			personType === PpdfSouhlasyTypOsobyEnum.FO
				? (step1.dataBoxes.find((item) => item.typSchranky === 'FO')
						?.datovaSchrankaId ?? '')
				: poDataBoxId,
		databoxTypes: ['FO', 'PFO', 'PO'],
	});

	const wizard = useWizard<ZapisForm>({
		steps: [{}, step2.form, step3],
		onSubmit: async (values, onSuccess) =>
			submitMutation.mutate(values, { onSuccess }),
	});

	const getRosContactByDataBoxId = (
		poDataBoxId: string
	): OsobyPodnikatelDto | undefined =>
		rosContacts.find((contact) => contact.datovaSchrankaId === poDataBoxId);

	return (
		<ServiceContext.Provider
			value={{
				isLoading:
					personQuery.isLoading ||
					step1.isLoading ||
					step2.isLoading ||
					toOvmOptionsIsLoading,
				noValuesForSharedAgendaData: step2.noValuesForSharedAgendaData,
				wizard,
				step1: step1.form,
				step2: step2.form,
				step3,
				selectedLabels: step2.selectedLabels,
				isSubmitting,
				person: personQuery.data,
				rosContacts,
				isNew: !consentId && !revoke,
				submit: (onSuccess) => {
					(
						wizard.step(
							2
						) as unknown as WizardFormStep<PpdfSouhlasyUdeleniFormStep3>
					).onSubmit(onSuccess)({} as BaseSyntheticEvent);
				},
				isPOTabVisible: step1.isPOTabVisible,
				loginInfo,
				getRosContactByDataBoxId,
				dataBoxes: step1.dataBoxes,
				consentValidityInterval: step2.form.formMethods.watch(
					'consentValidityInterval'
				),
				isLowAuthorization,
				isAuthorizationEnforced,
				rezaSwitcher,
			}}>
			{children}
		</ServiceContext.Provider>
	);
};

export const usePpdfSouhlasyUdeleniContext = () => {
	const context = useContext(ServiceContext);
	if (!context) {
		throw new Error('Context must be used within a ContextProvider');
	}
	return context;
};
